import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./rentalhouse.css";
import {
  ArrowLeft,
  Menu,
  X,
  ExternalLink,
  ShieldCheck,
  BookOpen,
  AlertTriangle,
  Search,
  Phone,
  MessageCircle,
  Mail,
  Heart,
  Grid2X2,
  List,
  ChevronDown,
  RotateCcw,
  MapPin,
  BadgeCheck,
  ArrowUpRight,
  CircleCheck,
  Home,
  CalendarDays,
  Wallet,
  KeyRound,
} from "lucide-react";

export default function RentHouse() {
  const navigate = useNavigate();

  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);

  /* =================================================
     FILTER STATES
  ================================================= */

  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [minRent, setMinRent] = useState("");
  const [maxRent, setMaxRent] = useState("");

  /* =================================================
     CONTACT STATES
  ================================================= */

  const [showConsult, setShowConsult] = useState(false);
  const [consultUser, setConsultUser] = useState(null);

  /* =================================================
     UI STATES
  ================================================= */

  const [showRentMenu, setShowRentMenu] = useState(false);

  const [viewMode, setViewMode] = useState("grid");

  const [favorites, setFavorites] = useState([]);

  const [rentSort, setRentSort] = useState("latest");

  /* =================================================
     USER
  ================================================= */

  const user = useMemo(() => {
    try {
      return JSON.parse(
        localStorage.getItem("civilink_user")
      );
    } catch {
      return null;
    }
  }, []);

  /* =================================================
     FETCH RENT HOUSES
  ================================================= */

  const fetchRentHouses = async (
    customFilters = {}
  ) => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      const currentSearch =
        customFilters.search !== undefined
          ? customFilters.search
          : search;

      const currentLocation =
        customFilters.location !== undefined
          ? customFilters.location
          : location;

      const currentMinRent =
        customFilters.minRent !== undefined
          ? customFilters.minRent
          : minRent;

      const currentMaxRent =
        customFilters.maxRent !== undefined
          ? customFilters.maxRent
          : maxRent;

      if (currentSearch) {
        params.append("q", currentSearch);
      }

      if (currentLocation) {
        params.append("location", currentLocation);
      }

      if (currentMinRent) {
        params.append("minRent", currentMinRent);
      }

      if (currentMaxRent) {
        params.append("maxRent", currentMaxRent);
      }

      const res = await fetch(
        `/api/rent/list?${params.toString()}`
      );

      if (!res.ok) {
        throw new Error(
          "Failed to fetch rental houses"
        );
      }

      const data = await res.json();

      if (data.success) {
        setHouses(data.rentHouses || []);
      } else {
        setHouses([]);
      }
    } catch (error) {
      console.error(
        "Rent house fetch error:",
        error
      );

      setHouses([]);
    } finally {
      setLoading(false);
    }
  };

  /* =================================================
     INITIAL FETCH
  ================================================= */

  useEffect(() => {
    fetchRentHouses();
  }, []);

  /* =================================================
     CONTACT OWNER
  ================================================= */

  const contactOwner = (owner) => {
    if (!localStorage.getItem("token")) {
      navigate("/");
      return;
    }

    setConsultUser(owner);
    setShowConsult(true);
  };

  /* =================================================
     FAVORITES
  ================================================= */

  const toggleFavorite = (houseId) => {
    setFavorites((prev) =>
      prev.includes(houseId)
        ? prev.filter((id) => id !== houseId)
        : [...prev, houseId]
    );
  };

  /* =================================================
     RESET FILTERS
  ================================================= */

  const resetFilters = () => {
    setSearch("");
    setLocation("");
    setMinRent("");
    setMaxRent("");
    setRentSort("latest");

    fetchRentHouses({
      search: "",
      location: "",
      minRent: "",
      maxRent: "",
    });
  };

  /* =================================================
     PRICE FORMAT
  ================================================= */

  const formatRent = (rent) => {
    const value = Number(rent);

    if (!value) {
      return "Rent on request";
    }

    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    }

    if (value >= 1000) {
      return `₹${Math.round(
        value / 1000
      )}K`;
    }

    return `₹${value.toLocaleString(
      "en-IN"
    )}`;
  };

  const formatFullRent = (rent) => {
    const value = Number(rent);

    if (!value) {
      return "";
    }

    return `₹${value.toLocaleString(
      "en-IN"
    )} / month`;
  };

  /* =================================================
     OWNER ID
  ================================================= */

  const getOwnerId = (house) => {
    return typeof house.postedBy === "object"
      ? house.postedBy?._id
      : house.postedBy;
  };

  /* =================================================
     AVAILABLE DATE
  ================================================= */

  const formatAvailableDate = (date) => {
    if (!date) {
      return null;
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate.toLocaleDateString(
      "en-IN",
      {
        day: "numeric",
        month: "short",
        year: "numeric",
      }
    );
  };

  /* =================================================
     RENTING GUIDE MENU
  ================================================= */

  const renderRentGuide = () => {
    if (!showRentMenu) {
      return null;
    }

    return (
      <div
        className="rent-menu-overlay"
        onClick={() =>
          setShowRentMenu(false)
        }
      >
        <aside
          className="rent-menu-panel"
          onClick={(e) =>
            e.stopPropagation()
          }
        >
          {/* HEADER */}

          <div className="rent-menu-header">
            <div>
              <span className="rent-menu-eyebrow">
                CIVILINK RENTAL GUIDE
              </span>

              <h2>
                Before You Rent
              </h2>

              <p>
                Simple guidance to help tenants
                understand rent, deposits,
                agreements and property checks.
              </p>
            </div>

            <button
              className="rent-menu-close"
              onClick={() =>
                setShowRentMenu(false)
              }
              aria-label="Close rental guide"
            >
              <X size={20} />
            </button>
          </div>

          {/* =================================================
              RENTAL CHECKLIST
          ================================================= */}

          <div className="rent-menu-section">
            <div className="rent-section-title">
              <CircleCheck size={17} />
              <span>
                Before Paying an Advance
              </span>
            </div>

            <div className="rent-checklist">

              <div>
                <CircleCheck size={15} />
                <span>
                  Visit and inspect the property
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Verify the owner's identity
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Confirm rent and deposit amount
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Discuss maintenance responsibilities
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Read the rental agreement carefully
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Confirm notice and termination terms
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Keep receipts for payments
                </span>
              </div>

              <div>
                <CircleCheck size={15} />
                <span>
                  Never transfer money solely based
                  on an online listing
                </span>
              </div>

            </div>
          </div>

          {/* =================================================
              RENTAL DOCUMENTS
          ================================================= */}

          <div className="rent-menu-section">
            <div className="rent-section-title">
              <BookOpen size={17} />
              <span>
                Important Rental Documents
              </span>
            </div>

            <div className="rent-guide-card">

              <div className="rent-guide-icon">
                <KeyRound size={18} />
              </div>

              <div>
                <strong>
                  Rental Agreement
                </strong>

                <p>
                  Check rent, deposit, duration,
                  notice period, maintenance and
                  other agreed terms.
                </p>
              </div>

            </div>

            <div className="rent-guide-card">

              <div className="rent-guide-icon">
                <Wallet size={18} />
              </div>

              <div>
                <strong>
                  Payment Records
                </strong>

                <p>
                  Keep records of advance,
                  deposit, rent and other payments.
                </p>
              </div>

            </div>

            <div className="rent-guide-card">

              <div className="rent-guide-icon">
                <Home size={18} />
              </div>

              <div>
                <strong>
                  Property Details
                </strong>

                <p>
                  Confirm address, facilities,
                  parking, utilities and property
                  condition before moving in.
                </p>
              </div>

            </div>

          </div>

          {/* =================================================
              TENANT CHECK
          ================================================= */}

          <div className="rent-menu-section">

            <div className="rent-section-title">
              <ShieldCheck size={17} />
              <span>
                Smart Tenant Checks
              </span>
            </div>

            <div className="rent-smart-card">

              <div className="smart-step">
                <span>
                  01
                </span>

                <div>
                  <strong>
                    Visit the property
                  </strong>

                  <p>
                    Don't rely only on photographs
                    or messages.
                  </p>
                </div>
              </div>

              <div className="smart-step">
                <span>
                  02
                </span>

                <div>
                  <strong>
                    Meet the owner
                  </strong>

                  <p>
                    Confirm who you are actually
                    dealing with.
                  </p>
                </div>
              </div>

              <div className="smart-step">
                <span>
                  03
                </span>

                <div>
                  <strong>
                    Read the agreement
                  </strong>

                  <p>
                    Understand every major financial
                    and occupancy condition.
                  </p>
                </div>
              </div>

              <div className="smart-step">
                <span>
                  04
                </span>

                <div>
                  <strong>
                    Pay securely
                  </strong>

                  <p>
                    Maintain proof of all payments.
                  </p>
                </div>
              </div>

            </div>

          </div>

          {/* =================================================
              OFFICIAL LINKS
          ================================================= */}

          <div className="rent-menu-section">

            <div className="rent-section-title">
              <ExternalLink size={17} />
              <span>
                Useful Official Resources
              </span>
            </div>

            <a
              href="https://www.tnreginet.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="rent-government-link"
            >
              <div className="rent-government-icon">
                <BookOpen size={18} />
              </div>

              <div>
                <strong>
                  TNREGINET
                </strong>

                <span>
                  Tamil Nadu Registration Department
                </span>
              </div>

              <ExternalLink size={15} />
            </a>

            <a
              href="https://www.tn.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="rent-government-link"
            >
              <div className="rent-government-icon">
                <ShieldCheck size={18} />
              </div>

              <div>
                <strong>
                  Tamil Nadu Government
                </strong>

                <span>
                  Official state government portal
                </span>
              </div>

              <ExternalLink size={15} />
            </a>

          </div>

          {/* =================================================
              IMPORTANT
          ================================================= */}

          <div className="rent-disclaimer">

            <AlertTriangle size={21} />

            <div>

              <strong>
                Important: Civilink is a listing
                platform
              </strong>

              <p>
                Civilink provides rental listings
                and communication facilities only.
                A property appearing on Civilink
                does not mean that Civilink has
                legally verified the property,
                owner or rental agreement.
              </p>

              <p>
                Tenants and owners should independently
                verify the property, identity,
                agreement terms and payment details
                before entering into a rental
                transaction.
              </p>

            </div>

          </div>

          <div className="rent-menu-footer">

            <ShieldCheck size={15} />

            <span>
              Stay alert, inspect the property and
              understand the agreement before paying.
            </span>

          </div>

        </aside>
      </div>
    );
  };

  /* =================================================
     MAIN
  ================================================= */

  return (
    <div className="rent-page">

      {/* =================================================
          TOP NAVIGATION
      ================================================= */}

      <div className="rent-topbar">

        <button
          className="rent-back-btn"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />

          <span>
            Back
          </span>
        </button>

        <button
          className="rent-guide-btn"
          onClick={() =>
            setShowRentMenu(true)
          }
        >
          <Menu size={18} />

          <span>
            Rental Guide
          </span>
        </button>

      </div>


      {/* =================================================
          HERO
      ================================================= */}

      <section className="rent-hero">

        <div className="rent-hero-overlay" />

        <div className="rent-hero-content">

          <div className="rent-badge">

            <Home size={15} />

            Rental Marketplace

          </div>

          <h1>

            Find a home
            <br />

            <span>
              that feels right.
            </span>

          </h1>

          <p>
            Explore rental homes from owners
            and find a place that fits your
            budget, location and lifestyle.
          </p>

          <div className="rent-trust-row">

            <div>
              <ShieldCheck size={17} />

              Trusted Listings
            </div>

            <div>
              <KeyRound size={17} />

              Flexible Rentals
            </div>

            <div>
              <MessageCircle size={17} />

              Direct Communication
            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          SEARCH
      ================================================= */}

      <section className="rent-search-wrapper">

        <div className="rent-search-card">

          {/* SEARCH */}

          <div className="rent-search-field">

            <Search size={19} />

            <div>

              <label>
                Search
              </label>

              <input
                placeholder="House, area or keyword"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchRentHouses();
                  }
                }}
              />

            </div>

          </div>


          {/* LOCATION */}

          <div className="rent-search-field">

            <MapPin size={19} />

            <div>

              <label>
                Location
              </label>

              <input
                placeholder="City or area"
                value={location}
                onChange={(e) =>
                  setLocation(e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchRentHouses();
                  }
                }}
              />

            </div>

          </div>


          {/* MIN RENT */}

          <div className="rent-search-field">

            <span className="rent-rupee">
              ₹
            </span>

            <div>

              <label>
                Min Rent
              </label>

              <input
                type="number"
                placeholder="Any"
                value={minRent}
                onChange={(e) =>
                  setMinRent(e.target.value)
                }
              />

            </div>

          </div>


          {/* MAX RENT */}

          <div className="rent-search-field">

            <span className="rent-rupee">
              ₹
            </span>

            <div>

              <label>
                Max Rent
              </label>

              <input
                type="number"
                placeholder="Any"
                value={maxRent}
                onChange={(e) =>
                  setMaxRent(e.target.value)
                }
              />

            </div>

          </div>


          {/* APPLY */}

          <button
            className="rent-search-btn"
            onClick={() =>
              fetchRentHouses()
            }
          >
            <Search size={18} />

            Search
          </button>


          {/* RESET */}

          <button
            className="rent-reset-btn"
            onClick={resetFilters}
          >
            <RotateCcw size={15} />

            Reset
          </button>

        </div>

      </section>


      {/* =================================================
          QUICK RENT FILTERS
      ================================================= */}

      <section className="rent-quick-section">

        <div className="rent-quick-filters">

          <button
            className={
              minRent === "" &&
              maxRent === ""
                ? "active"
                : ""
            }
            onClick={() => {
              setMinRent("");
              setMaxRent("");

              fetchRentHouses({
                minRent: "",
                maxRent: "",
              });
            }}
          >
            All Rentals
          </button>

          <button
            className={
              maxRent === "10000"
                ? "active"
                : ""
            }
            onClick={() => {
              setMinRent("");
              setMaxRent("10000");

              fetchRentHouses({
                minRent: "",
                maxRent: "10000",
              });
            }}
          >
            Under ₹10K
          </button>

          <button
            className={
              minRent === "10000" &&
              maxRent === "20000"
                ? "active"
                : ""
            }
            onClick={() => {
              setMinRent("10000");
              setMaxRent("20000");

              fetchRentHouses({
                minRent: "10000",
                maxRent: "20000",
              });
            }}
          >
            ₹10K – ₹20K
          </button>

          <button
            className={
              minRent === "20000"
                ? "active"
                : ""
            }
            onClick={() => {
              setMinRent("20000");
              setMaxRent("");

              fetchRentHouses({
                minRent: "20000",
                maxRent: "",
              });
            }}
          >
            ₹20K+
          </button>

          <button
            className="rent-guide-chip"
            onClick={() =>
              setShowRentMenu(true)
            }
          >
            <BookOpen size={16} />

            Rental Guide
          </button>

        </div>

      </section>


      {/* =================================================
          RESULTS HEADER
      ================================================= */}

      <section className="rent-results-header">

        <div>

          <p className="rent-results-label">
            RENTAL MARKETPLACE
          </p>

          <h2>
            {loading
              ? "Finding rentals..."
              : `${houses.length} Rental ${
                  houses.length === 1
                    ? "Home"
                    : "Homes"
                }`}
          </h2>

        </div>


        <div className="rent-results-controls">

          {/* VIEW */}

          <div className="rent-view-toggle">

            <button
              className={
                viewMode === "grid"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setViewMode("grid")
              }
            >
              <Grid2X2 size={18} />
            </button>

            <button
              className={
                viewMode === "list"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setViewMode("list")
              }
            >
              <List size={19} />
            </button>

          </div>


          {/* SORT */}

          <div className="rent-sort-wrapper">

            <select
              value={rentSort}
              onChange={(e) =>
                setRentSort(e.target.value)
              }
            >
              <option value="latest">
                Latest First
              </option>

              <option value="rentLow">
                Rent: Low → High
              </option>

              <option value="rentHigh">
                Rent: High → Low
              </option>
            </select>

            <ChevronDown size={16} />

          </div>

        </div>

      </section>


      {/* =================================================
          RENTAL LIST
      ================================================= */}

      <main
        className={
          viewMode === "grid"
            ? "rent-list"
            : "rent-list rent-list-view"
        }
      >

        {loading ? (

          <div className="rent-loading-state">

            <div className="rent-loading-spinner" />

            <h3>
              Finding rental homes...
            </h3>

            <p>
              Searching available homes for you.
            </p>

          </div>

        ) : houses.length === 0 ? (

          <div className="rent-empty-state">

            <div className="rent-empty-icon">
              <Search size={29} />
            </div>

            <h3>
              No rental homes found
            </h3>

            <p>
              Try changing your location or
              monthly rent range.
            </p>

            <button
              onClick={resetFilters}
            >
              <RotateCcw size={16} />

              Reset Filters
            </button>

          </div>

        ) : (

          houses.map((h) => {

            const ownerId =
              getOwnerId(h);

            const isOwnListing =
              user &&
              ownerId &&
              ownerId === user._id;

            const availableDate =
              formatAvailableDate(
                h.availableFrom
              );

            return (

              <article
                key={h._id}
                className="rent-card"
              >

                {/* =================================================
                    IMAGE
                ================================================= */}

                <div className="rent-image-wrapper">

                  {h.imageUrl ? (

                    <img
                      src={h.imageUrl}
                      alt={
                        h.title ||
                        "Rental house"
                      }
                      className="rent-image"
                      loading="lazy"
                    />

                  ) : (

                    <div className="rent-image-placeholder">

                      <Home size={40} />

                      <span>
                        Rental Home
                      </span>

                    </div>

                  )}

                  <div className="rent-image-gradient" />


                  {/* RENTAL BADGE */}

                  <span className="rent-property-badge">

                    <KeyRound size={12} />

                    For Rent

                  </span>


                  {/* FAVORITE */}

                  <button
                    className={`rent-favorite-btn ${
                      favorites.includes(h._id)
                        ? "liked"
                        : ""
                    }`}
                    onClick={() =>
                      toggleFavorite(h._id)
                    }
                    aria-label="Save rental"
                  >
                    <Heart
                      size={19}
                      fill={
                        favorites.includes(
                          h._id
                        )
                          ? "currentColor"
                          : "none"
                      }
                    />
                  </button>

                </div>


                {/* =================================================
                    INFO
                ================================================= */}

                <div className="rent-info">

                  <div className="rent-title-row">

                    <div>

                      <h3>
                        {h.title ||
                          "Rental Home"}
                      </h3>

                      <p className="rent-location">

                        <MapPin size={15} />

                        {h.location?.city ||
                          "Location"}

                        {h.location?.state
                          ? `, ${h.location.state}`
                          : ""}

                      </p>

                    </div>

                    <ArrowUpRight
                      className="rent-card-arrow"
                      size={20}
                    />

                  </div>


                  {/* PRICE */}

                  <div className="rent-price-row">

                    <div>

                      <strong>
                        {formatRent(h.rent)}
                      </strong>

                      <span>
                        {formatFullRent(
                          h.rent
                        )}
                      </span>

                    </div>

                  </div>


                  {/* RENT DETAILS */}

                  <div className="rent-details">

                    {h.deposit > 0 && (

                      <div>

                        <Wallet size={14} />

                        <span>
                          Deposit ₹
                          {Number(
                            h.deposit
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </span>

                      </div>

                    )}

                    {availableDate && (

                      <div>

                        <CalendarDays
                          size={14}
                        />

                        <span>
                          Available{" "}
                          {availableDate}
                        </span>

                      </div>

                    )}

                  </div>


                  {/* OWNER */}

                  <div className="rent-owner-row">

                    <div className="rent-owner-profile">

                      <div className="rent-owner-avatar">

                        {h.postedBy?.photoURL ? (

                          <img
                            src={
                              h.postedBy
                                .photoURL
                            }
                            alt={
                              h.postedBy
                                ?.name ||
                              "Owner"
                            }
                          />

                        ) : (

                          <span>
                            {(
                              h.postedBy
                                ?.name ||
                              "O"
                            )
                              .charAt(0)
                              .toUpperCase()}
                          </span>

                        )}

                      </div>


                      <div>

                        <strong>
                          {h.postedBy?.name ||
                            "Property Owner"}
                        </strong>

                        <span>
                          <BadgeCheck
                            size={13}
                          />

                          Property Owner
                        </span>

                      </div>

                    </div>


                    {/* OWNER ACTION */}

                    {isOwnListing ? (

                      <button
                        className="rent-own-btn"
                        disabled
                      >
                        Your Listing
                      </button>

                    ) : (

                      <button
                        className="rent-contact-btn"
                        onClick={() =>
                          contactOwner(
                            h.postedBy
                          )
                        }
                      >
                        Contact
                      </button>

                    )}

                  </div>

                </div>

              </article>

            );
          })

        )}

      </main>


      {/* =================================================
          RENTAL TRUST STRIP
      ================================================= */}

      <section className="rent-trust-strip">

        <div className="rent-trust-item">

          <div className="rent-trust-icon">
            <Home size={21} />
          </div>

          <div>

            <strong>
              Visit Before Renting
            </strong>

            <span>
              Inspect the actual property
            </span>

          </div>

        </div>


        <div className="rent-trust-item">

          <div className="rent-trust-icon">
            <Wallet size={21} />
          </div>

          <div>

            <strong>
              Understand Payments
            </strong>

            <span>
              Rent, deposit and other charges
            </span>

          </div>

        </div>


        <div className="rent-trust-item">

          <div className="rent-trust-icon">
            <KeyRound size={21} />
          </div>

          <div>

            <strong>
              Read Agreement
            </strong>

            <span>
              Know your rental terms
            </span>

          </div>

        </div>


        <div className="rent-trust-item">

          <div className="rent-trust-icon">
            <ShieldCheck size={21} />
          </div>

          <div>

            <strong>
              Rent Carefully
            </strong>

            <span>
              Verify before paying
            </span>

          </div>

        </div>

      </section>


      {/* =================================================
          RENT GUIDE
      ================================================= */}

      {renderRentGuide()}


      {/* =================================================
          CONTACT OWNER SHEET
      ================================================= */}

      {showConsult && (

        <div
          className="customer-sheet-overlay"
          onClick={() =>
            setShowConsult(false)
          }
        >

          <div
            className="customer-sheet"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="sheet-top">

              <div className="sheet-handle" />

              <button
                className="sheet-close"
                onClick={() =>
                  setShowConsult(false)
                }
                aria-label="Close"
              >
                <X size={20} />
              </button>

            </div>


            <p className="sheet-eyebrow">
              CIVILINK RENTAL
            </p>

            <h3 className="sheet-title">
              Contact Owner
            </h3>

            <p className="sheet-subtitle">
              Choose how you'd like to
              connect.
            </p>


            {/* OWNER */}

            <div className="consult-profile">

              <div className="consult-avatar">

                {consultUser?.photoURL ? (

                  <img
                    src={
                      consultUser.photoURL
                    }
                    alt={
                      consultUser.name ||
                      "Owner"
                    }
                  />

                ) : (

                  <span>
                    {(
                      consultUser?.name ||
                      "O"
                    )
                      .charAt(0)
                      .toUpperCase()}
                  </span>

                )}

              </div>


              <div>

                <h4>
                  {consultUser?.name ||
                    "Property Owner"}
                </h4>

                <p>
                  <BadgeCheck size={14} />

                  Property Owner
                </p>

              </div>

            </div>


            {/* ACTIONS */}

            <div className="contact-actions">

              {consultUser?.phone && (

                <>

                  {/* CALL */}

                  <button
                    className="contact-action"
                    onClick={() => {

                      window.location.href =
                        `tel:${consultUser.phone}`;

                      setShowConsult(false);

                    }}
                  >

                    <div className="contact-action-icon phone">
                      <Phone size={20} />
                    </div>

                    <div>

                      <strong>
                        Call Owner
                      </strong>

                      <span>
                        {consultUser.phone}
                      </span>

                    </div>

                    <ArrowUpRight
                      size={18}
                    />

                  </button>


                  {/* WHATSAPP */}

                  <button
                    className="contact-action"
                    onClick={() => {

                      const text =
                        encodeURIComponent(
                          `Hello ${
                            consultUser.name ||
                            ""
                          },

I found your rental property on Civilink and would like to know more about it.`
                        );

                      const cleanPhone =
                        String(
                          consultUser.phone
                        ).replace(
                          /\D/g,
                          ""
                        );

                      const whatsappNumber =
                        cleanPhone.startsWith(
                          "91"
                        )
                          ? cleanPhone
                          : `91${cleanPhone}`;

                      window.open(
                        `https://wa.me/${whatsappNumber}?text=${text}`,
                        "_blank"
                      );

                      setShowConsult(false);

                    }}
                  >

                    <div className="contact-action-icon whatsapp">
                      <MessageCircle
                        size={20}
                      />
                    </div>

                    <div>

                      <strong>
                        WhatsApp
                      </strong>

                      <span>
                        Chat directly with owner
                      </span>

                    </div>

                    <ArrowUpRight
                      size={18}
                    />

                  </button>

                </>

              )}


              {/* EMAIL */}

              {consultUser?.email && (

                <button
                  className="contact-action"
                  onClick={() => {

                    window.location.href =
                      `mailto:${consultUser.email}?subject=Rental Inquiry from Civilink`;

                    setShowConsult(false);

                  }}
                >

                  <div className="contact-action-icon email">
                    <Mail size={20} />
                  </div>

                  <div>

                    <strong>
                      Email Owner
                    </strong>

                    <span>
                      {consultUser.email}
                    </span>

                  </div>

                  <ArrowUpRight
                    size={18}
                  />

                </button>

              )}

            </div>


            {/* UNAVAILABLE */}

            {!consultUser?.phone &&
              !consultUser?.email && (

                <div className="contact-unavailable">

                  Owner contact information
                  is not available.

                </div>

              )}


            {/* CLOSE */}

            <button
              className="sheet-cancel-btn"
              onClick={() =>
                setShowConsult(false)
              }
            >
              Cancel
            </button>

          </div>

        </div>

      )}

    </div>
  );
}